import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { editorTopicsAPI } from 'api/editor/topics';
import { getEditUrl, getViewUrl } from 'tools/tools';
import useBundleTranslation from 'i18n';
import { Topic } from 'components/common/element-data/types';

export function useMentionTermTooltip() {
    const [containerElement, setContainerElement] = useState<any>(null);
    const [currentItemId, setCurrentItemId] = useState<number>(0);
    const [tooltipInfo, setTooltipInfo] = useState<any>({});
    const [tooltipAnchor, setTooltipAnchor] = useState<any>(null);

    const { data: mentionGlossaryTermData, isLoading: glossaryTermTooltipDataLoading } = useQuery<any, Error>({
        queryKey: [`mention-glossary-term-tooltip-info--${currentItemId}`],
        queryFn: async () => {
            setTooltipInfo({ ...tooltipInfo, [currentItemId]: 'LOADING' });
            return await editorTopicsAPI.getTopicsInfo([currentItemId]);
        },
        enabled: currentItemId > 0 && !tooltipInfo[currentItemId],
        staleTime: 15 * 60 * 1000, //15 minutes
    });

    useEffect(() => {
        const response = mentionGlossaryTermData;
        if (response && response.status == 'OK') {
            if (response.data && Object.values(response.data).length > 0) {
                const section: any = Object.values(response.data)[0];
                const topicInfo = section?.topics?.[0];
                if (topicInfo) {
                    setTooltipInfo({ ...tooltipInfo, [currentItemId]: topicInfo });
                } else {
                    setTooltipInfo({ ...tooltipInfo, [currentItemId]: 'REMOVED' });
                }
            } else {
                setTooltipInfo({ ...tooltipInfo, [currentItemId]: 'REMOVED' });
            }
        }
    }, [mentionGlossaryTermData]);

    const onMouseOver = (e: any) => {
        const term = e.target.closest('.mention-item');
        if (!term || !containerElement.contains(term)) return;

        const id = Number(term.getAttribute('data-id'));
        const type = term.getAttribute('data-type');

        if (type != 'glossary_term') return;
        setCurrentItemId(id);
        setTooltipAnchor(term);
    };
    const onMouseOut = (e: any) => {
        const from = e.target.closest('.mention-item');
        const to = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.mention-item');
        const tooltip =
            e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.mention-item-term-tooltip');
        if (!from || from === to || tooltip) return;
        setCurrentItemId(0);
        setTooltipAnchor(null);
    };

    const hideTooltip = () => {
        setCurrentItemId(0);
        setTooltipAnchor(null);
    };

    useEffect(() => {
        if (containerElement) {
            containerElement.addEventListener('mouseover', onMouseOver);
            containerElement.addEventListener('mouseout', onMouseOut);
            containerElement.classList.toggle('mention-term-tooltip-behavior', true);
        }
        return () => {
            if (containerElement) {
                containerElement.removeEventListener('mouseover', onMouseOver);
                containerElement.removeEventListener('mouseout', onMouseOut);
            }
        };
    }, [containerElement]);

    return {
        mentionGlossaryTermTooltipAnchor: tooltipAnchor,
        mentionGlossaryTermTooltipInfo: tooltipInfo?.[currentItemId],
        hideMentionGlossaryTermTooltip: hideTooltip,
        setMentionGlossaryTermTooltipContainer: setContainerElement,
    };
}

interface MentionItemLinkProps {
    behaviorType?: 'view' | 'editor';
    handleTopicClick?: (isDefault: boolean, topic: Topic) => void;
    onMentionItemLinkClick?: (id: number, type: string, mentionItemData: any) => void;
    availableClickGlossaryTermsIds?: number[];
}
export function useMentionItemLink(props: MentionItemLinkProps) {
    const {
        behaviorType = 'view',
        handleTopicClick,
        onMentionItemLinkClick = () => {},
        availableClickGlossaryTermsIds,
    } = props;
    const { t } = useBundleTranslation();
    const [containerElement, setContainerElement] = useState<any>(null);
    const mentionItemLinkData = useRef<any>({});

    const setMentionItemLinkData = (data: any) => {
        mentionItemLinkData.current = data;
    };
    const openLinkNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };
    const showWarnElementDoesNotExist = () => {
        alert(t('this_element_does_not_exist'));
    };
    const onClick = (e: any) => {
        const term = e.target.closest('.mention-item');
        if (!term || !containerElement.contains(term)) return;

        const id = Number(term.getAttribute('data-id'));
        const type = term.getAttribute('data-type');

        const elementData = mentionItemLinkData.current?.[`${type}_${id}`];
        onMentionItemLinkClick(id, type, elementData);

        if (behaviorType === 'view') {
            switch (type) {
                case 'glossary_term':
                    if (handleTopicClick) {
                        if (!elementData) {
                            showWarnElementDoesNotExist();
                        } else {
                            handleTopicClick(false, { name: 'glossary_term', topicId: id });
                        }
                    }
                    break;
                case 'element':
                    if (!elementData) {
                        showWarnElementDoesNotExist();
                    } else {
                        openLinkNewTab(getViewUrl(elementData.element_type) + id);
                    }
                    break;
            }
        } else if (behaviorType === 'editor') {
            if (!elementData) {
                showWarnElementDoesNotExist();
                return false;
            }

            switch (type) {
                case 'glossary_term':
                    if (handleTopicClick) {
                        handleTopicClick(false, { name: 'glossary_term', topicId: id });
                    } else {
                        openLinkNewTab(`/editor/glossary/${id}`);
                    }
                    break;
                case 'element':
                    openLinkNewTab(getEditUrl(elementData.element_type) + id);
                    break;
                case 'user_group':
                    openLinkNewTab(`/editor/group/${id}`);
                    break;
            }
        }
    };

    useEffect(() => {
        if (containerElement) {
            containerElement.addEventListener('click', onClick);
            const className = 'mention-term-link-behavior--' + behaviorType;
            containerElement.classList.toggle(className, true);

            if (availableClickGlossaryTermsIds) {
                containerElement.querySelectorAll('.mention-item[data-type="glossary_term"]').forEach((term: any) => {
                    if (!availableClickGlossaryTermsIds.includes(Number(term.getAttribute('data-id')))) {
                        term.classList.add('mention-item--no-click');
                    }
                });
            }
        }
        return () => {
            if (containerElement) {
                containerElement.removeEventListener('click', onClick);
            }
        };
    }, [containerElement]);

    return {
        setMentionItemLinkData,
        setMentionItemLinkContainer: setContainerElement,
    };
}
